/* =========================================================================
   INO x Horizon - ino-home.js
   Behavior layer for the bespoke INO content sections.
   - Idempotent: safe to call init() many times (theme editor re-renders).
   - Theme-editor safe: re-wires on shopify:section:load, cleans up on unload.
   - Defensive: every node is guarded, missing elements never throw.
   - Respects prefers-reduced-motion (CSS owns the reveal transition).
   ========================================================================= */
(function () {
  "use strict";

  /* Single shared IntersectionObserver for reveal-on-scroll. Created once,
     reused for every section so re-init never spawns duplicates. */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || typeof IntersectionObserver === "undefined") {
      return revealObserver;
    }
    revealObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          if (e.isIntersecting) {
            e.target.classList.add("in");
            revealObserver.unobserve(e.target);
          }
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -60px 0px" }
    );
    return revealObserver;
  }

  /* Normalize the root argument: init may be called with a section element,
     a document, or nothing. Always return something with querySelectorAll. */
  function asScope(root) {
    if (!root) return document;
    if (root.querySelectorAll) return root;
    return document;
  }

  /* Run a callback for the root itself (if it matches) plus all matching
     descendants. Lets us treat a single section root the same as document. */
  function eachMatch(scope, selector, cb) {
    if (scope.matches && scope.matches(selector)) cb(scope);
    var nodes = scope.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) cb(nodes[i]);
  }

  /* ---------------------------------------------------------------------
     a. Reveal-on-scroll
     --------------------------------------------------------------------- */
  function initReveals(scope) {
    var io = getRevealObserver();
    eachMatch(scope, ".ino-reveal", function (el) {
      if (el.dataset.inoReveal) return;
      el.dataset.inoReveal = "1";
      if (io) {
        io.observe(el);
      } else {
        /* No IntersectionObserver support: just show it. */
        el.classList.add("in");
      }
    });
  }

  /* ---------------------------------------------------------------------
     b. Nav scroll state  (id "ino-nav" -> .scrolled when scrollY > 60)
     --------------------------------------------------------------------- */
  function initNav() {
    var nav = document.getElementById("ino-nav");
    if (!nav || nav.dataset.inoBound) return;
    nav.dataset.inoBound = "1";

    function updateNav() {
      var current = document.getElementById("ino-nav");
      if (!current) return;
      if (window.scrollY > 60) current.classList.add("scrolled");
      else current.classList.remove("scrolled");
    }
    window.addEventListener("scroll", updateNav, { passive: true });
    updateNav();
  }

  /* ---------------------------------------------------------------------
     c. Sticky cart  (id "stickyCart" -> .show past hero, not over footer)
     --------------------------------------------------------------------- */
  function initStickyCart() {
    var sticky = document.getElementById("stickyCart");
    if (!sticky || sticky.dataset.inoBound) return;
    sticky.dataset.inoBound = "1";

    var shown = false;
    function updateSticky() {
      var el = document.getElementById("stickyCart");
      if (!el) return;
      var hero = document.querySelector(".ino-hero");
      var footer = document.querySelector(".ino-footer");
      var past = hero
        ? window.scrollY > hero.offsetHeight - 100
        : window.scrollY > 0;
      var overFooter = false;
      if (footer) {
        var fRect = footer.getBoundingClientRect();
        overFooter = fRect.top < window.innerHeight - 40;
      }
      var next = past && !overFooter;
      if (next !== shown) {
        shown = next;
        el.classList.toggle("show", next);
      }
    }
    window.addEventListener("scroll", updateSticky, { passive: true });
    window.addEventListener("resize", updateSticky);
    updateSticky();
  }

  /* ---------------------------------------------------------------------
     e. Howfolds sticky image swap
        .step[data-step] intersects -> matching .sticky img[data-step].active
        Scoped per .ino-howfolds container, one observer each.
     --------------------------------------------------------------------- */
  function initHowfolds(scope) {
    if (typeof IntersectionObserver === "undefined") return;
    eachMatch(scope, ".ino-howfolds", function (container) {
      if (container.dataset.inoBound) return;
      container.dataset.inoBound = "1";

      var images = container.querySelectorAll(".sticky img[data-step]");
      var stepIO = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            var e = entries[i];
            if (e.isIntersecting) {
              var n = e.target.dataset.step;
              for (var j = 0; j < images.length; j++) {
                images[j].classList.toggle(
                  "active",
                  images[j].dataset.step === n
                );
              }
            }
          }
        },
        { threshold: 0.55 }
      );

      var steps = container.querySelectorAll(".step[data-step]");
      for (var k = 0; k < steps.length; k++) stepIO.observe(steps[k]);
    });
  }

  /* ---------------------------------------------------------------------
     f. Reviews carousel
        .arrows button[data-dir="prev"|"next"] scroll .track by 90% width.
     --------------------------------------------------------------------- */
  function initCarousels(scope) {
    eachMatch(scope, ".ino-carousel", function (carousel) {
      if (carousel.dataset.inoBound) return;
      carousel.dataset.inoBound = "1";

      var track = carousel.querySelector(".track");
      if (!track) return;
      var buttons = carousel.querySelectorAll(".arrows button[data-dir]");
      for (var i = 0; i < buttons.length; i++) {
        (function (btn) {
          btn.addEventListener("click", function () {
            var dir = btn.getAttribute("data-dir") === "prev" ? -1 : 1;
            /* RTL (Hebrew market): the track scrolls right-to-left, so flip the
               scroll direction - otherwise prev/next would scroll the wrong way. */
            if (getComputedStyle(track).direction === "rtl") dir = -dir;
            track.scrollBy({
              left: dir * track.clientWidth * 0.9,
              behavior: "smooth",
            });
          });
        })(buttons[i]);
      }
    });
  }

  /* ---------------------------------------------------------------------
     f1b. Carousel drag
        No arrows: the track is dragged with the mouse, the same way the
        product gallery works. Touch already scrolls natively, so this is
        mouse-only. A drag must NOT also count as a click, or dragging
        across a review card would start its video.
     --------------------------------------------------------------------- */
  function initCarouselDrag(scope) {
    eachMatch(scope, ".ino-carousel .track", function (track) {
      if (track.dataset.inoDrag) return;
      track.dataset.inoDrag = "1";

      var down = false, startX = 0, startScroll = 0, moved = false;

      track.addEventListener("pointerdown", function (e) {
        if (e.pointerType !== "mouse") return;
        down = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        track.classList.add("dragging");
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      });

      track.addEventListener("pointermove", function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 3) moved = true;
        track.scrollLeft = startScroll - dx;
      });

      function endDrag() {
        if (!down) return;
        down = false;
        track.classList.remove("dragging");
      }
      track.addEventListener("pointerup", endDrag);
      track.addEventListener("pointercancel", endDrag);

      /* Capture phase: swallow the click that ends a drag before it reaches
         a card (which would otherwise play its video). */
      track.addEventListener("click", function (e) {
        if (!moved) return;
        moved = false;
        e.preventDefault();
        e.stopPropagation();
      }, true);
    });
  }

  /* ---------------------------------------------------------------------
     f2. Review videos
        The clips carry no audio track at all, which is exactly the case a
        browser allows to start on its own. So a card runs its own silent
        loop while it is on screen and pauses the moment it leaves - nothing
        is downloaded until the card is close, and nothing keeps decoding
        off screen. Clicking (or Enter/Space) pauses and resumes it.

        Falls back to the old press-to-play badge whenever the loop is not
        wanted or not possible: the section's "Play the clips automatically"
        switch is off, the visitor asked their device for reduced motion, the
        browser has no IntersectionObserver, or it simply refuses to start
        (iOS Low Power Mode, data saver). In every one of those cases the
        photo stays up and the badge comes back, never a dead black card.
     --------------------------------------------------------------------- */
  var revResume = [];        /* one entry per auto card - see the tab-return note below */
  var revResumeBound = false;

  function initReviewVideos(scope) {
    var reduced = false;
    try {
      reduced = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch (err) {}

    eachMatch(scope, "[data-ino-revplay]", function (media) {
      if (media.dataset.inoRevplay) return;
      media.dataset.inoRevplay = "1";

      var card = media.closest ? media.closest(".vcard") : null;
      var video = media.querySelector(".rev-video");
      if (!card || !video) return;

      var auto = media.hasAttribute("data-ino-revauto") && !reduced && "IntersectionObserver" in window;
      var held = false;      /* the visitor pressed pause - do not auto-resume */
      var onScreen = false;  /* kept in step by the observer further down */
      var fetched = false;

      video.muted = true; /* the attribute is in the markup; Safari also wants the property */
      card.classList.add("v-ready");
      if (auto) card.classList.add("v-auto");

      /* load() restarts the element, so it may only ever run once. */
      function fetchClip() {
        if (fetched) return;
        fetched = true;
        try { video.preload = "auto"; video.load(); } catch (err) {}
      }

      function play() {
        fetchClip();
        video.muted = true;
        card.classList.remove("v-paused");
        var started = video.play();
        if (started && typeof started.catch === "function") {
          started.catch(function (err) {
            /* Not every rejection is a refusal. An AbortError only means this
               particular attempt was interrupted - the visitor scrolled
               straight past, or the tab went to the background - and the card
               is still perfectly able to play. Treating that as "autoplay is
               blocked" would kill the loop for everyone who scrolls fast.
               So look at the element a beat later and only hand the card back
               if it really is sitting still, in view, refusing to start. */
            if (err && err.name === "AbortError") return;
            setTimeout(function () {
              if (!video.paused || !onScreen || held) return;
              auto = false;
              card.classList.remove("v-auto");
              if (!video.currentTime) card.classList.remove("is-playing");
            }, 400);
          });
        }
      }

      function pause(byVisitor) {
        try { video.pause(); } catch (err) {}
        if (byVisitor) {
          held = true;
          card.classList.add("v-paused");
        }
      }

      /* Only cover the photo once the clip is genuinely running. */
      video.addEventListener("playing", function () {
        card.classList.add("is-playing");
      });

      function toggle() {
        if (video.paused) {
          held = false;
          play();
        } else {
          pause(true);
        }
      }

      media.addEventListener("click", function (e) {
        e.preventDefault();
        toggle();
      });

      media.addEventListener("keydown", function (e) {
        var k = e.key;
        if (k !== "Enter" && k !== " " && k !== "Spacebar") return;
        e.preventDefault();
        toggle();
      });

      if (!auto) return;

      /* Start fetching a little before the card arrives, so the loop is
         already moving by the time it is read. */
      var near = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            fetchClip();
            near.disconnect();
            return;
          }
        }
      }, { rootMargin: "400px" });
      near.observe(card);

      /* Run only while at least half the card is on screen. A card scrolled
         out sideways in the carousel is clipped by the rail, so it counts as
         off screen too - only what the visitor can see is moving. */
      var seen = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var visible = entries[i].isIntersecting && entries[i].intersectionRatio >= 0.5;
          onScreen = visible;
          if (visible) {
            if (!held && auto) play();
          } else {
            pause(false);
          }
        }
      }, { threshold: [0, 0.5, 0.75] });
      seen.observe(card);

      /* Switching to another tab makes the browser suspend a silent clip, and
         coming back moves nothing on the page - so no observer fires and the
         card would sit frozen. Pick it up again by hand on return. */
      revResume.push(function () {
        if (!card.isConnected) return false;
        if (auto && !held && onScreen && video.paused) play();
        return true;
      });
    });

    if (!revResumeBound) {
      revResumeBound = true;
      document.addEventListener("visibilitychange", function () {
        if (document.hidden) return;
        var alive = [];
        for (var i = 0; i < revResume.length; i++) {
          try {
            if (revResume[i]()) alive.push(revResume[i]);
          } catch (err) {}
        }
        revResume = alive;
      });
    }
  }

  /* ---------------------------------------------------------------------
     g2. Technology spec sheet - staggered reveal + count-up on numbers.
        [data-ino-specsheet] intersects -> .in (CSS staggers the rows in),
        then every ".sv .n" that is a pure number counts up from 0.
     --------------------------------------------------------------------- */
  function countUpOne(el) {
    var finalText = el.textContent;
    if (!/^\d+$/.test(finalText)) return; /* only pure numbers animate */
    var target = parseInt(finalText, 10);
    if (typeof requestAnimationFrame === "undefined") {
      el.textContent = target;
      return;
    }
    var startTs = null,
      dur = 1100;
    function tick(now) {
      if (startTs === null) startTs = now;
      var p = Math.min(1, (now - startTs) / dur);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(target * eased);
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  function initSpecsheets(scope) {
    eachMatch(scope, "[data-ino-specsheet]", function (sheet) {
      if (sheet.dataset.inoBound) return;
      sheet.dataset.inoBound = "1";

      function run() {
        sheet.classList.add("in");
        var nums = sheet.querySelectorAll(".sv .n");
        for (var j = 0; j < nums.length; j++) countUpOne(nums[j]);
      }

      if (typeof IntersectionObserver === "undefined") {
        run();
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              run();
              io.unobserve(entries[i].target);
            }
          }
        },
        { threshold: 0.25 }
      );
      io.observe(sheet);
    });
  }

  /* ---------------------------------------------------------------------
     g. Hero word animation
        Split each .ino-hero h1 text into <span class="w w-N">word</span>,
        preserving <br>, so the CSS @keyframes wordRise runs.
        Cap explicit w-N delay classes; fall back to inline animation-delay.
     --------------------------------------------------------------------- */
  var MAX_DELAY_CLASS = 8; /* w-1 .. w-8 have CSS delays; beyond -> inline */

  function splitHeading(h1) {
    if (h1.dataset.inoSplit) return;
    h1.dataset.inoSplit = "1";

    /* Walk child nodes so we keep <br> boundaries intact. */
    var sourceNodes = Array.prototype.slice.call(h1.childNodes);
    var frag = document.createDocumentFragment();
    var index = 0;

    for (var n = 0; n < sourceNodes.length; n++) {
      var node = sourceNodes[n];

      /* Skip comment nodes (e.g. Shopify design-mode setting markers) so their
         text is never turned into visible words. */
      if (node.nodeType === 8) continue;

      if (node.nodeType === 1 && node.tagName === "BR") {
        frag.appendChild(document.createElement("br"));
        continue;
      }

      var text =
        node.nodeType === 3
          ? node.nodeValue
          : node.textContent || "";
      var words = text.split(/\s+/);

      for (var w = 0; w < words.length; w++) {
        var word = words[w];
        if (!word) continue;
        index++;
        var span = document.createElement("span");
        span.className = "w w-" + index;
        span.textContent = word;
        if (index > MAX_DELAY_CLASS) {
          /* Stagger continues past the predefined classes. */
          span.style.animationDelay = index * 0.08 + "s";
        }
        frag.appendChild(span);
        frag.appendChild(document.createTextNode(" "));
      }
    }

    if (index > 0) {
      h1.textContent = "";
      h1.appendChild(frag);
    }
  }

  function initHero(scope) {
    /* In the theme editor, Shopify injects <!--BEGIN/END setting--> comment
       markers around inline_richtext values. Splitting the heading would expose
       them as visible text, so skip the word animation in design mode. The live
       storefront has no such markers and still animates normally. */
    if (window.Shopify && window.Shopify.designMode) return;
    eachMatch(scope, ".ino-hero", function (hero) {
      var headings = hero.querySelectorAll("h1");
      for (var i = 0; i < headings.length; i++) splitHeading(headings[i]);
    });
  }

  /* ---------------------------------------------------------------------
     init(root): (re)wire everything. Optional root scopes the work to one
     section. Document-level handlers (nav/sticky/escape) bind once globally.
     --------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------
     Nav glue: search opens Horizon's search modal, the Bag pill opens
     Horizon's cart drawer, the hamburger toggles the INO mobile menu.
     All document-level + idempotent (dataset.inoBound).
     --------------------------------------------------------------------- */
  function initNavMenu() {
    var searchBtns = document.querySelectorAll("[data-ino-search]");
    for (var i = 0; i < searchBtns.length; i++) {
      (function (btn) {
        if (btn.dataset.inoBound) return;
        btn.dataset.inoBound = "1";
        btn.addEventListener("click", function (e) {
          var modal = document.querySelector("#search-modal");
          if (modal && typeof modal.showDialog === "function") {
            e.preventDefault();
            modal.showDialog();
          }
        });
      })(searchBtns[i]);
    }

    var cartBtns = document.querySelectorAll("[data-ino-cart]");
    for (var j = 0; j < cartBtns.length; j++) {
      (function (btn) {
        if (btn.dataset.inoBound) return;
        btn.dataset.inoBound = "1";
        btn.addEventListener("click", function (e) {
          var drawer = document.querySelector("cart-drawer-component");
          if (drawer && typeof drawer.open === "function") {
            e.preventDefault();
            drawer.open();
          }
        });
      })(cartBtns[j]);
    }

    var burgers = document.querySelectorAll("[data-ino-burger]");
    for (var k = 0; k < burgers.length; k++) {
      (function (btn) {
        if (btn.dataset.inoBound) return;
        btn.dataset.inoBound = "1";
        var panel = document.querySelector("[data-ino-mobile]");
        function closeMenu() {
          if (!panel) return;
          panel.classList.remove("open");
          btn.setAttribute("aria-expanded", "false");
          document.body.style.overflow = "";
        }
        btn.addEventListener("click", function () {
          if (!panel) return;
          var willOpen = !panel.classList.contains("open");
          panel.classList.toggle("open", willOpen);
          btn.setAttribute("aria-expanded", willOpen ? "true" : "false");
          document.body.style.overflow = willOpen ? "hidden" : "";
        });
        if (panel && !panel.dataset.inoBound) {
          panel.dataset.inoBound = "1";
          /* Close the menu when any item inside it is tapped (links, close
             button, and the search/account actions). The search action also
             triggers the global [data-ino-search] handler that opens the modal. */
          var items = panel.querySelectorAll("a, button");
          for (var m = 0; m < items.length; m++) items[m].addEventListener("click", closeMenu);
        }
        document.addEventListener("keydown", function (e) {
          if (e.key === "Escape") closeMenu();
        });
      })(burgers[k]);
    }
  }

  function init(root) {
    var scope = asScope(root);
    try { initReveals(scope); } catch (err) {}
    try { initNav(); } catch (err) {}
    try { initStickyCart(); } catch (err) {}
    try { initHowfolds(scope); } catch (err) {}
    try { initCarousels(scope); } catch (err) {}
    try { initCarouselDrag(scope); } catch (err) {}
    try { initReviewVideos(scope); } catch (err) {}
    try { initSpecsheets(scope); } catch (err) {}
    try { initHero(scope); } catch (err) {}
    try { initNavMenu(); } catch (err) {}
  }

  /* Initial run. */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* ---------------------------------------------------------------------
     Theme editor lifecycle.
     - section:load  -> re-init the loaded section subtree.
     - section:unload-> if its portal was open, restore body scroll.
     --------------------------------------------------------------------- */
  document.addEventListener("shopify:section:load", function (e) {
    var target = (e && e.target) || document;
    init(target);
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var target = e && e.target;
    if (!target || !target.querySelector) return;
    /* If the unloaded section held the currently open tech portal, make sure
       we don't leave the page scroll-locked. */
    if (
      openPortal &&
      (target === openPortal || target.contains(openPortal))
    ) {
      document.body.style.overflow = "";
      openPortal = null;
    }
  });
})();
